<script setup lang="ts">
/**
 * One menu item, created or edited, in a side sheet.
 *
 * Everything a till needs to sell it: name, category, where it is made, the
 * price the guest is told, the tax category the rate resolves from, allergens,
 * 18+, and the modifier groups it offers. Saving writes the draft; nothing a
 * till sees moves until the menu is published.
 */
import { computed, reactive, ref } from "vue";
import type { DraftGroup, DraftItem, DraftMenu, ItemInput } from "../api";
import { ALLERGENS, DIETARY_TAGS } from "@gf/contracts/values";

const props = defineProps<{
  /** Null creates a new item. */
  item: DraftItem | null;
  draft: DraftMenu;
  t: (key: string) => string;
  busy: boolean;
  /** Where a new item lands when the list is filtered to a category. */
  defaultCategoryId: string | null;
  /** Shrinks and uploads a photo, answering its sha-256. Absent: no photo control. */
  upload?: (file: File) => Promise<string>;
  /** Where a stored photo is shown from. */
  photoUrl?: (sha: string) => string;
}>();

const emit = defineEmits<{
  save: [input: ItemInput];
  cancel: [];
}>();

const TAX = ["FOOD", "NON_ALC_BEVERAGE", "ALCOHOL", "TAP_WATER", "MILK_BEVERAGE", "NON_FOOD"] as const;

/** Minor units to what a manager types: 22900 → "229,00". No float ever holds it. */
const toText = (minor: bigint): string => {
  const s = minor.toString().padStart(3, "0");
  return `${s.slice(0, -2)},${s.slice(-2)}`;
};

const form = reactive({
  name: props.item?.name ?? "",
  categoryId: props.item?.categoryId ?? props.defaultCategoryId ?? "",
  stationId: props.item?.stationId ?? props.draft.stations[0]?.stationId ?? "",
  taxCategory: props.item?.taxCategory ?? "FOOD",
  price: props.item ? toText(props.item.grossMinor) : "",
  description: props.item?.description ?? "",
  allergens: [...(props.item?.allergens ?? [])] as string[],
  ageRestricted: props.item?.ageRestricted ?? false,
  active: props.item?.active ?? true,
  groupIds: [...(props.item?.modifierGroupIds ?? [])] as string[],
  // What a guest sees of it online.
  photoSha: props.item?.photoSha ?? null,
  nameEn: props.item?.translations?.en?.name ?? "",
  descriptionEn: props.item?.translations?.en?.description ?? "",
  dietary: [...(props.item?.dietary ?? [])] as string[],
  featured: props.item?.featured ?? false,
});

const uploading = ref(false);
const photoError = ref(false);
const choosePhoto = async (event: Event): Promise<void> => {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file || !props.upload) return;
  uploading.value = true;
  photoError.value = false;
  try {
    form.photoSha = await props.upload(file);
  } catch {
    photoError.value = true;
  } finally {
    uploading.value = false;
  }
};
const toggleDiet = (tag: string): void => {
  const at = form.dietary.indexOf(tag);
  if (at >= 0) form.dietary.splice(at, 1);
  else form.dietary.push(tag);
};

/** "229", "229,5", "229.50" → 22950n; anything else is null. */
const priceMinor = computed<bigint | null>(() => {
  const cleaned = form.price.replace(/\s/g, "").replace(",", ".");
  if (!/^\d{1,9}(\.\d{1,2})?$/.test(cleaned)) return null;
  const [whole, frac = ""] = cleaned.split(".");
  return BigInt(`${whole!}${frac.padEnd(2, "0")}`);
});

const valid = computed(
  () => form.name.trim().length > 0 && form.stationId !== "" && priceMinor.value !== null,
);

const toggleAllergen = (code: string): void => {
  const at = form.allergens.indexOf(code);
  if (at >= 0) form.allergens.splice(at, 1);
  else form.allergens.push(code);
};

const toggleGroup = (groupId: string): void => {
  const at = form.groupIds.indexOf(groupId);
  if (at >= 0) form.groupIds.splice(at, 1);
  else form.groupIds.push(groupId);
};

const groupRule = (g: DraftGroup): string =>
  g.minSelections > 0 ? props.t("menu.required") : props.t("menu.optional");

const save = (): void => {
  if (!valid.value || priceMinor.value === null) return;
  emit("save", {
    name: form.name.trim(),
    categoryId: form.categoryId === "" ? null : form.categoryId,
    stationId: form.stationId,
    taxCategory: form.taxCategory,
    grossMinor: priceMinor.value,
    description: form.description.trim() === "" ? null : form.description.trim(),
    allergens: ALLERGENS.filter((a) => form.allergens.includes(a)),
    ageRestricted: form.ageRestricted,
    active: form.active,
    modifierGroupIds: [...form.groupIds],
    photoSha: form.photoSha,
    // English is the one the office edits; other languages the venue already
    // had are kept as they were.
    translations: {
      ...(props.item?.translations ?? {}),
      en: {
        ...(form.nameEn.trim() ? { name: form.nameEn.trim() } : {}),
        ...(form.descriptionEn.trim() ? { description: form.descriptionEn.trim() } : {}),
      },
    },
    dietary: DIETARY_TAGS.filter((d) => form.dietary.includes(d)),
    featured: form.featured,
  });
};
</script>

<template>
  <aside class="gf-drawer sheet" role="dialog" aria-modal="false" :aria-label="item?.name ?? t('menu.newItem')">
    <button type="button" class="gf-drawer__close" :aria-label="t('menu.cancel')" @click="emit('cancel')">
      ×
    </button>
    <h2 class="gf-drawer__title">{{ item ? item.name : t("menu.newItem") }}</h2>

    <form class="sheet__form" @submit.prevent="save">
      <label class="gf-field">
        <span class="gf-label">{{ t("menu.field.name") }}</span>
        <input v-model="form.name" class="gf-input" type="text" maxlength="120" data-field="name" />
      </label>

      <div class="sheet__pair">
        <label class="gf-field">
          <span class="gf-label">{{ t("menu.field.price") }}</span>
          <input
            v-model="form.price"
            class="gf-input gf-num"
            type="text"
            inputmode="decimal"
            placeholder="0,00"
            data-field="price"
          />
          <span class="sheet__hint">{{ t("menu.priceHint") }}</span>
        </label>
        <label class="gf-field">
          <span class="gf-label">{{ t("menu.taxCategory") }}</span>
          <select v-model="form.taxCategory" class="gf-input" data-field="taxCategory">
            <option v-for="code in TAX" :key="code" :value="code">{{ t(`menu.tax.${code}`) }}</option>
          </select>
        </label>
      </div>

      <div class="sheet__pair">
        <label class="gf-field">
          <span class="gf-label">{{ t("menu.category") }}</span>
          <select v-model="form.categoryId" class="gf-input" data-field="category">
            <option value="">{{ t("menu.uncategorised") }}</option>
            <option v-for="c in draft.categories" :key="c.categoryId" :value="c.categoryId">
              {{ c.name }}
            </option>
          </select>
        </label>
        <label class="gf-field">
          <span class="gf-label">{{ t("menu.station") }}</span>
          <select v-model="form.stationId" class="gf-input" data-field="station">
            <option v-for="s in draft.stations" :key="s.stationId" :value="s.stationId">{{ s.name }}</option>
          </select>
        </label>
      </div>

      <label class="gf-field">
        <span class="gf-label">{{ t("menu.field.description") }}</span>
        <textarea v-model="form.description" class="gf-input sheet__textarea" rows="2" maxlength="280" />
      </label>

      <fieldset class="sheet__fieldset">
        <legend class="gf-label">{{ t("menu.allergens") }}</legend>
        <div class="sheet__toggles">
          <button
            v-for="code in ALLERGENS"
            :key="code"
            type="button"
            class="gf-button sheet__toggle"
            :aria-pressed="form.allergens.includes(code)"
            :data-allergen="code"
            @click="toggleAllergen(code)"
          >
            {{ t(`allergen.${code}`) }}
          </button>
        </div>
      </fieldset>

      <fieldset class="sheet__fieldset">
        <legend class="gf-label">{{ t("menu.field.groups") }}</legend>
        <p v-if="draft.modifierGroups.length === 0" class="gf-muted">{{ t("menu.noGroups") }}</p>
        <div v-else class="sheet__toggles">
          <button
            v-for="g in draft.modifierGroups.filter((x) => x.active)"
            :key="g.groupId"
            type="button"
            class="gf-button sheet__toggle"
            :aria-pressed="form.groupIds.includes(g.groupId)"
            :data-group="g.groupId"
            @click="toggleGroup(g.groupId)"
          >
            {{ g.name }} <span class="gf-muted">· {{ groupRule(g) }}</span>
          </button>
        </div>
      </fieldset>

      <div class="sheet__checks">
        <label class="sheet__check">
          <input v-model="form.ageRestricted" type="checkbox" data-field="age" />
          {{ t("menu.field.ageRestricted") }}
        </label>
        <label class="sheet__check">
          <input v-model="form.active" type="checkbox" data-field="active" />
          {{ t("menu.field.active") }}
        </label>
      </div>

      <fieldset class="sheet__guests" data-section="guests">
        <legend class="gf-label">{{ t("menu.guests") }}</legend>
        <div v-if="upload" class="sheet__photo">
          <img v-if="form.photoSha && photoUrl" :src="photoUrl(form.photoSha)" :alt="form.name" class="sheet__photoImg" data-testid="photo" />
          <div class="sheet__photoActions">
            <label class="gf-button">
              {{ t("menu.photoUpload") }}
              <input type="file" accept="image/*" class="sheet__file" data-field="photo" :disabled="uploading" @change="choosePhoto" />
            </label>
            <button v-if="form.photoSha" type="button" class="gf-button gf-button--ghost" @click="form.photoSha = null">{{ t("menu.photoRemove") }}</button>
            <span class="sheet__hint">{{ photoError ? t("menu.photoFailed") : t("menu.photoHint") }}</span>
          </div>
        </div>
        <label class="gf-field">
          <span class="gf-label">{{ t("menu.nameEn") }}</span>
          <input v-model="form.nameEn" class="gf-input" type="text" maxlength="120" data-field="name-en" />
        </label>
        <label class="gf-field">
          <span class="gf-label">{{ t("menu.descriptionEn") }}</span>
          <textarea v-model="form.descriptionEn" class="gf-input" rows="2" maxlength="600" data-field="description-en" />
        </label>
        <div class="gf-field">
          <span class="gf-label">{{ t("menu.dietary") }}</span>
          <div class="gf-chips">
            <button
              v-for="d in DIETARY_TAGS"
              :key="d"
              type="button"
              class="gf-chip"
              :class="{ 'gf-chip--accent': form.dietary.includes(d) }"
              :aria-pressed="form.dietary.includes(d)"
              :data-diet="d"
              @click="toggleDiet(d)"
            >
              {{ t(`menu.diet.${d}`) }}
            </button>
          </div>
        </div>
        <label class="sheet__check">
          <input v-model="form.featured" type="checkbox" data-field="featured" />
          {{ t("menu.featured") }}
        </label>
      </fieldset>

      <div class="sheet__actions">
        <button type="button" class="gf-button" @click="emit('cancel')">{{ t("menu.cancel") }}</button>
        <button type="submit" class="gf-button gf-button--primary" :disabled="!valid || busy || uploading" data-testid="save-item">
          {{ t("menu.save") }}
        </button>
      </div>
    </form>
  </aside>
</template>

<style scoped>
.sheet {
  width: min(34rem, 100vw);
}

.sheet__form {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-4);
}

.sheet__pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--gf-space-3);
}

.sheet__hint {
  font-size: var(--gf-text-xs);
  color: var(--gf-color-text-muted);
}

.sheet__textarea {
  padding-top: var(--gf-space-2);
  padding-bottom: var(--gf-space-2);
  resize: vertical;
}

.sheet__fieldset {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-2);
  margin: 0;
  padding: 0;
  border: 0;
}

.sheet__toggles {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gf-space-1);
}

.sheet__toggle {
  min-height: 32px;
  padding: 0 var(--gf-space-2);
  font-size: var(--gf-text-sm);
}

.sheet__checks {
  display: flex;
  gap: var(--gf-space-5);
}

.sheet__check {
  display: flex;
  align-items: center;
  gap: var(--gf-space-2);
}

.sheet__guests {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-3);
  margin: 0;
  padding: var(--gf-space-4) 0 0;
  border: 0;
  border-top: var(--gf-border-thin) solid var(--gf-color-border-subtle);
}

.sheet__photo {
  display: flex;
  gap: var(--gf-space-3);
  align-items: flex-start;
}

.sheet__photoImg {
  width: 8rem;
  aspect-ratio: 4 / 3;
  border-radius: var(--gf-radius-md);
  object-fit: cover;
}

.sheet__photoActions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--gf-space-2);
}

.sheet__file {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.sheet__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--gf-space-2);
  padding-top: var(--gf-space-4);
  border-top: var(--gf-border-thin) solid var(--gf-color-border-subtle);
}
</style>
